import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { FetchPolicy } from 'utils/enum/Core';
import {
    makeStyles, Button,
    Dialog, DialogContent,
    DialogActions, Tooltip,
    Checkbox,
} from '@material-ui/core';
import Split from 'react-split';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import ModalUtils from 'utils/ModalUtils';
import DateUtils from 'lib/DateUtils';
import StringUtils from 'lib/StringUtils';
import { Form, Row } from 'react-bootstrap';
import ButtonStyles from 'styles/theme/Button';
import UserQuery from 'services/graphQL/query/UserQuery';
import AIQuery from 'services/graphQL/query/ai/AIQuery';
import AIMutation from 'services/graphQL/mutate/ai/AIMutation';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import VirtualTable from 'components/widgets/VirtualTable';
import Select from 'components/widgets/Select';
import AIConversationSpot from 'components/modules/ai/AIConversationSpot';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    main: {
        padding: '10px',
        display: 'flex',
        flexDirection: 'column',
        '& > div:nth-child(1)': {
            fontWeight: 'bold',
            marginLeft: '15px',
            fontSize: '18px',
            marginBottom: '30px',
            flexShrink: 0,
        },
        '& > div:nth-child(2)': {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginLeft: '15px',
            marginRight: '15px',
            alignItems: 'center',
            '& > div:nth-child(2)': {
                marginLeft: 0,
                marginRight: 0,
                marginBottom: 0,
                '& > label': {
                    marginBottom: 0,
                    marginRight: '10px',
                    fontSize: '14px',
                },
                '& > span': {
                    padding: 0,
                },
            },
        },
    },
    tableHeader: {
        textAlign: 'left',
        color: theme.palette.text.waterloo,
        borderRight: `1px solid ${theme.palette.border.ghost}`,
        height: '100%',
        alignItems: 'center',
    },
    tableContainer: {
        marginTop: '15px',
        paddingLeft: '15px',
        paddingRight: '15px',
        height: 'auto',
        overflow: 'hidden',
        flexGrow: 1,
        '& > div': {
            overflow: 'hidden',
        },
    },
    text: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    link: {
        color: theme.palette.link,
        textDecoration: 'underline',
        cursor: 'pointer',
    },
    AppBar: {
        color: theme.palette.text.white,
        backgroundColor: theme.palette.background.sanMarino,
        '& h4': {
            color: theme.palette.text.white,
            fontSize: '15px',
        },
    },
    content: {
        padding: '20px 10px',
        [theme.breakpoints.down('sm')]: {
            paddingLeft: 0,
            paddingRight: 0,
        },
        '& > div:nth-child(1)': {
            marginBottom: '20px',
            fontSize: '14px',
            lineHeight: 1.5,
            height: '360px',
            overflowY: 'auto',
            whiteSpace: 'pre-line',
        },
        '& > div:nth-child(2) > textarea': {
            resize: 'none',
        },
    },
    input: {
        fontSize: '13px',
        width: '300px',
        '& > div:nth-child(1)': {
            fontSize: '13px !important',
        },
    },
    splitter: {
        display: 'flex',
        overflow: 'hidden',
        height: '100%',
        width: '100%',
    },
    parent: {
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
        '& > div:nth-child(1)': {
            width: '50%',
        },
        '& > div:nth-child(2)': {
            width: '50%',
            height: '500px',
            marginBottom: '15px',
        },
    },
}));

const EmployeePerformanceReport = ({
    pullAllReports,
    height,
}) => {
    const classes = { ...useStyles(), ...buttonStyles() };
    const [state, setState] = useState({
        reports: {
            data: [],
            total: 0,
        },
        employees: [],
        params: {
            init: 0,
            limit: 50,
        },
        selectedReport: null,
        isReportOpen: false,
        selectedEmployee: -1,
        onlyFlagged: false,
    });

    const {
        reports,
        params,
        selectedReport,
        isReportOpen,
        employees,
        selectedEmployee,
        onlyFlagged,
    } = state;

    const filters = [];
    if (pullAllReports && selectedEmployee !== -1) filters.push({ columnName: 'employeeId', values: String(selectedEmployee) });
    if (pullAllReports && onlyFlagged) filters.push({ columnName: 'flagged', values: 'true' });

    const {
        data: reportsData,
        loading: loadingReports,
        error: errorLoadingReports,
    } = useQuery(pullAllReports ? AIQuery.GET_ALL_EMPLOYEE_REPORTS : AIQuery.GET_EMPLOYEE_REPORTS, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        variables: {
            paginate: {
                init: params.init,
                limit: params.limit,
            },
            ...(filters.length > 0 ? {
                filters,
            } : {}),
        },
    });

    const {
        data: usersData,
        loading: loadingUsersData,
        error: errorLoadingUsersData,
    } = useQuery(UserQuery.GET_USERS_BY_COMPANY, {
        variables: {
            search: state.globalSearch,
        },
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    const updateReport = (id, data) => {
        setState((prevState) => (
            {
                ...prevState,
                reports: {
                    ...prevState.reports,
                    data: prevState.reports.data.map((i) => {
                        if (i.employeeAnalysisReportId === id) {
                            const clone = { ...i };
                            Object.keys(data).forEach((k) => { clone[k] = data[k]; });

                            return clone;
                        }

                        return i;
                    }),
                },
            }
        ));
    };

    const [submitReport, { loading: submittingReport }] = useMutation(AIMutation.SUBMIT_EMPLOYEE_REPORT, {
        onCompleted: (response) => {
            if (response) {
                const { employeeAnalysisReportId, comments } = selectedReport;
                updateReport(employeeAnalysisReportId, { read: true, comments });
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    useEffect(() => {
        if (errorLoadingReports) {
            ModalUtils.errorMessage(errorLoadingReports?.graphQLErrors);
            return;
        }

        if (!loadingReports) {
            const reps = pullAllReports ? reportsData?.getAllEmployeeReports : reportsData?.getEmployeeReports;
            if (reps) {
                setState((prevState) => ({
                    ...prevState,
                    reports: { data: [...prevState.reports.data, ...reps.data], total: reps.total },
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingReports, errorLoadingReports]);

    useEffect(() => {
        if (errorLoadingUsersData) {
            ModalUtils.errorMessage(errorLoadingUsersData?.graphQLErrors);
            return;
        }

        if (!loadingUsersData) {
            const users = usersData?.getUsersByCompany;
            if (users) {
                setState((prevState) => ({
                    ...prevState,
                    employees: users.sort((a, b) => a.firstName.localeCompare(b.firstName)),
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingUsersData, errorLoadingUsersData]);

    const loadMoreData = () => {
        setState((prevState) => ({
            ...prevState,
            params: {
                ...prevState.params,
                init: reports.data.length,
            },
        }));
    };

    const toggleReport = (report) => {
        if (!isReportOpen && report && !pullAllReports) {
            const { employeeAnalysisReportId, comments } = report;
            submitReport({
                variables: {
                    employeeAnalysisReportId,
                    read: true,
                    comments,
                },
            });
        }

        setState((prevState) => ({
            ...prevState,
            isReportOpen: !prevState.isReportOpen,
            ...(report ? { selectedReport: report } : {}),
        }));
    };

    const onChangeComments = (comments) => {
        setState((prevState) => ({
            ...prevState,
            selectedReport: {
                ...prevState.selectedReport,
                comments,
            },
        }));
    };

    const onSaveComments = () => {
        const { employeeAnalysisReportId, read, comments } = selectedReport;
        submitReport({
            variables: {
                employeeAnalysisReportId,
                read,
                comments,
            },
        });
    };

    const onChange = (name, value) => {
        setState((prevState) => ({
            ...prevState,
            [name]: value,
            reports: {
                data: [],
                total: 0,
            },
        }));
    };

    const columns = [
        {
            headerClassName: classes.tableHeader,
            label: '',
            dataKey: 'number',
            width: 80,
            cellRenderer: (cell) => {
                const { rowIndex } = cell;

                return (
                    <span>{rowIndex + 1}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Report',
            dataKey: 'report',
            width: pullAllReports ? 200 : 400,
            cellRenderer: (cell) => {
                const {
                    rowData: record,
                } = cell;

                return (
                    <span
                        className={clsx(classes.text, classes.link)}
                        onClick={() => toggleReport(record)}
                    >
                        Click to see your report...
                    </span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Comments',
            dataKey: 'comments',
            width: pullAllReports ? 250 : 500,
            cellRenderer: (cell) => {
                const {
                    rowData: record,
                } = cell;

                return (
                    <span className={classes.text}>{record.comments}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Read',
            dataKey: 'read',
            width: 80,
            cellRenderer: (cell) => {
                const {
                    rowData: record,
                } = cell;

                return (
                    <span className={classes.text}>{record.read ? 'Yes' : 'No'}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Created On',
            dataKey: 'createdOn',
            width: 170,
            cellRenderer: (cell) => {
                const {
                    rowData: record,
                } = cell;

                return (
                    <span className={classes.text}>{DateUtils.getFormattedDateInUserTimezone(record.createdOn)}</span>
                );
            },
        },
    ];

    if (pullAllReports) {
        columns.splice(1, 0, {
            headerClassName: classes.tableHeader,
            label: 'Employee',
            dataKey: 'employeeId',
            width: 150,
            cellRenderer: (cell) => {
                const {
                    rowData: record,
                } = cell;

                const employee = employees.find((e) => Number(e.userId) === Number(record.employeeId));
                return (
                    <span className={classes.text}>{employee ? `${employee.firstName} ${employee.lastName}` : ''}</span>
                );
            },
        });

        columns.splice(4, 0, {
            headerClassName: classes.tableHeader,
            label: 'Flagged',
            dataKey: 'flagged',
            width: 80,
            cellRenderer: (cell) => {
                const {
                    rowData: record,
                } = cell;

                return (
                    <span className={classes.text}>{record.flagged ? 'Yes' : 'No'}</span>
                );
            },
        });

        columns.splice(5, 0, {
            headerClassName: classes.tableHeader,
            label: 'Flagged Reason',
            dataKey: 'flaggedReason',
            width: 200,
            cellRenderer: (cell) => {
                const {
                    rowData: record,
                } = cell;

                return (
                    <Tooltip title={record.flaggedReason ?? ''}>
                        <span className={classes.text}>{record.flaggedReason}</span>
                    </Tooltip>
                );
            },
        });
    }

    const reportContent = () => (
        <div className={classes.content}>
            <div>
                {selectedReport.report}
            </div>
            <div>
                <Form.Control
                    as="textarea"
                    size="md"
                    rows="4"
                    placeholder="Comments"
                    value={selectedReport.comments ?? ''}
                    onChange={(e) => onChangeComments(e.target.value)}
                    disabled={pullAllReports}
                />
            </div>
        </div>
    );

    const threadId = reports.data?.length > 0 ? reports.data[0].threadId : null;
    return (
        <>
            <div className={classes.main} style={{ height: height ?? '100%' }}>
                <div>Employee Performance Reports</div>
                <div>
                    {pullAllReports && (
                        <>
                            <Select
                                nowrap
                                size="sm"
                                loading={loadingUsersData}
                                className={classes.input}
                                name="selectedEmployee"
                                value={selectedEmployee}
                                options={[{
                                    userId: -1,
                                    firstName: 'All',
                                    lastName: 'Employess',
                                }, ...employees].map(({
                                    userId,
                                    firstName,
                                    lastName,
                                    employeeType,
                                }) => ({
                                    value: userId,
                                    label: StringUtils
                                        .toPascalCase(`${firstName?.toLowerCase()} ${lastName?.toLowerCase()} ${employeeType ? `(${employeeType})` : ''}`),
                                }))}
                                onChange={(name, value) => onChange(name, value)}
                            />
                            <Form.Group as={Row}>
                                <Form.Label>Flagged</Form.Label>
                                <Checkbox
                                    color="default"
                                    disableRipple
                                    checked={onlyFlagged}
                                    onChange={(_, value) => onChange('onlyFlagged', value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </div>
                {pullAllReports && (
                    <div className={clsx(classes.tableContainer, 'virtual-table-base')}>
                        <VirtualTable
                            loading={loadingReports}
                            rowHeight={45}
                            totalRecords={reports.total}
                            data={reports.data}
                            columns={columns}
                            loadMore={loadMoreData}
                        />
                    </div>
                )}
                {!pullAllReports && (
                    <Split
                        sizes={[50, 50]}
                        minSize={300}
                        gutterSize={10}
                        gutterAlign="center"
                        direction="horizontal"
                        cursor="col-resize"
                        className={classes.splitter}
                    >
                        <div className={clsx(classes.tableContainer, 'virtual-table-base')}>
                            <VirtualTable
                                loading={loadingReports}
                                rowHeight={45}
                                totalRecords={reports.total}
                                data={reports.data}
                                columns={columns}
                                loadMore={loadMoreData}
                            />
                        </div>
                        <AIConversationSpot threadId={threadId} />
                    </Split>
                )}
            </div>
            {isReportOpen && (
                <Dialog
                    open
                    fullWidth
                    maxWidth={pullAllReports ? 'lg' : 'sm'}
                    disableBackdropClick
                    disableEscapeKeyDown
                    scroll="paper"
                    onMouseDown={(e) => e.stopPropagation()}
                    disableEnforceFocus
                >
                    <DialogAppBar
                        appBarClassName={classes.AppBar}
                        title="Performance Report"
                        onClose={() => toggleReport()}
                        toolbarSize="md"
                    />
                    <DialogContent>
                        {pullAllReports && (
                            <div className={classes.parent}>
                                {reportContent()}
                                <div>
                                    <AIConversationSpot readOnly threadId={selectedReport.threadId} />
                                </div>
                            </div>
                        )}
                        {!pullAllReports && (
                            reportContent()
                        )}
                    </DialogContent>
                    {!pullAllReports && (
                        <DialogActions>
                            <Button
                                disabled={submittingReport}
                                className={classes.containedSecondaryInfo}
                                size="small"
                                onClick={onSaveComments}
                            >
                                Save
                            </Button>
                        </DialogActions>
                    )}
                </Dialog>
            )}
        </>
    );
};

EmployeePerformanceReport.defaultProps = {
    pullAllReports: false,
    height: null,
};

EmployeePerformanceReport.propTypes = {
    pullAllReports: PropTypes.bool,
    height: PropTypes.string,
};

export default EmployeePerformanceReport;
